import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getSuggestions,
  createSuggestion,
  updateSuggestion,
  deleteSuggestion,
  type CreateSuggestionInput,
  type UpdateSuggestionInput,
} from '@/services/api/suggestions'

/**
 * Fetch all suggestions for the current user
 */
export const useSuggestions = () => {
  return useQuery({
    queryKey: ['suggestions'],
    queryFn: getSuggestions,
    refetchOnWindowFocus: true,
  })
}

/**
 * Create a new suggestion
 */
export const useCreateSuggestion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateSuggestionInput) => createSuggestion(input),
    onSuccess: () => {
      // Invalidate and refetch suggestions
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}

/**
 * Update an existing suggestion
 */
export const useUpdateSuggestion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSuggestionInput }) =>
      updateSuggestion(id, input),
    onSuccess: () => {
      // Invalidate and refetch suggestions
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}

/**
 * Delete a suggestion
 */
export const useDeleteSuggestion = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteSuggestion(id),
    onSuccess: () => {
      // Invalidate and refetch suggestions
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}
